import React from 'react';
import { Box } from '@mui/material';
import { Clock, ArrowRight } from 'lucide-react';
import { Booking, VehicleType } from './types';
import { getVehicleTypeColor } from './vehicleColors';
import { themeColors } from './themeColors';

interface BookingBlockProps {
  booking: Booking;
  left: number;
  width: number;
  height: number;
  vehicleType: VehicleType;
}

export function BookingBlock({ booking, left, width, height, vehicleType }: BookingBlockProps) {
  const colors = getVehicleTypeColor(vehicleType);
  
  // Determine trip completion status
  const now = new Date();
  const isCompleted = booking.endTime < now;
  const isInProgress = booking.startTime <= now && booking.endTime >= now;
  
  // Get block colors based on vehicle type and status
  const getBlockColors = () => {
    const opacity = isCompleted ? 0.4 : 1;
    
    switch (booking.status) {
      case 'confirmed':
        return {
          bgcolor: colors.primary.bgcolor,
          color: colors.text.color,
          border: `1px solid ${colors.border.borderColor}`,
          opacity
        };
      case 'inprogress':
        return {
          bgcolor: colors.primary.bgcolor,
          color: colors.text.color,
          border: `1px solid ${colors.border.borderColor}`,
          opacity
        };
      case 'tentative':
        return {
          bgcolor: '#ffffff',
          color: colors.lightText.color,
          border: `2px dashed ${colors.tentative.borderColor}`,
          opacity
        };
      case 'waiting':
        return {
          bgcolor: colors.waiting.bgcolor,
          color: colors.lightText.color,
          border: `1px solid ${colors.waitingBorder.borderColor}`,
          opacity
        };
      case 'reposition':
        return {
          bgcolor: colors.reposition.bgcolor,
          color: colors.repositionText.color,
          border: `1px solid ${colors.repositionBorder.borderColor}`,
          opacity
        };
      default:
        return {
          bgcolor: colors.light.bgcolor,
          color: colors.lightText.color,
          border: `1px solid ${colors.lightBorder.borderColor}`,
          opacity
        };
    }
  };
  
  const blockColors = getBlockColors();
  
  const formatTime = (date: Date) => {
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  };
  
  const formatDateTime = (date: Date) => {
    const day = date.getDate();
    const month = date.toLocaleString('en-US', { month: 'short' });
    const time = formatTime(date);
    const dateStr = `${day}-${month}`;
    return { day, month, time, dateStr };
  };
  
  const getCityIndicator = (city: 'Pune' | 'Mumbai') => {
    return city === 'Pune' ? 'P' : 'M';
  };
  
  const startInfo = formatDateTime(booking.startTime);
  const endInfo = formatDateTime(booking.endTime);
  const pickupIndicator = getCityIndicator(booking.pickupCity);
  const dropIndicator = getCityIndicator(booking.dropCity);
  const statusLabel = booking.status.toUpperCase();

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 4,
        left,
        width: Math.max(width, 40),
        height,
        borderRadius: 1,
        px: 1,
        py: 0,
        fontSize: '0.75rem',
        overflow: 'hidden',
        transition: 'opacity 0.2s',
        ...blockColors
      }}
            // title={`${booking.description}\n${formatTime(booking.startTime)} - ${formatTime(booking.endTime)}\n${booking.pickupCity} → ${booking.dropCity}\nStatus: ${statusLabel}`}
      title={`${booking.description}\n${formatTime(booking.startTime)} - ${formatTime(booking.endTime)}\n${booking.pickupCity} → ${booking.dropCity}${isCompleted ? '\n[COMPLETED]' : isInProgress ? '\n[IN PROGRESS]' : ''}`}
    >
      <Box sx={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', height: '100%', position: 'relative' }}>
        {/* In-progress indicator bar */}
        {isInProgress && (
          <Box sx={{
            position: 'absolute',
            inset: 0,
            border: '2px solid #fbbf24',
            borderRadius: 1,
            pointerEvents: 'none'
          }} />
        )}
        
        {/* Start indicator */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'start', flexShrink: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{
              fontWeight: 700,
              fontSize: '0.875rem',
              bgcolor: 'rgba(255,255,255,0.2)',
              px:0,
              borderRadius: 0.5
            }}>
              {pickupIndicator}
            </Box>
            {booking.status === 'reposition' && (
              <ArrowRight style={{ width: 12, height: 12, flexShrink: 0 }} />
            )}
          </Box>
          <Box sx={{ fontSize: '0.75rem', lineHeight: 1.2, mt: 0.5, fontWeight: 600 }}>
            {startInfo.dateStr}
          </Box>
          <Box sx={{ fontSize: '0.75rem', lineHeight: 1.2, fontWeight: 700 }}>
            {startInfo.time}
          </Box>
        </Box>
        
        {/* Center content - only show if there's enough space */}
        {width > 200 && (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', px: 1, minWidth: 0 }}>
            {booking.bookingId && (
              <Box component="span" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%', textAlign: 'center' }}>
                {booking.bookingId}
              </Box>
            )}
            {/* {booking.customerName && (
              <Box sx={{ fontSize: '0.625rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%', textAlign: 'center' }}>
                {booking.customerName}
              </Box>
            )} */}
          </Box>
        )}
        
        {/* End indicator - only show if there's enough space */}
        {width > 100 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'end', flexShrink: 0 }}>
            <Box sx={{
              fontWeight: 700,
              fontSize: '0.875rem',
              bgcolor: 'rgba(255,255,255,0.2)',
              px: 0.5,
              borderRadius: 0.5
            }}>
              {dropIndicator}
            </Box>
            <Box sx={{ fontSize: '0.75rem', lineHeight: 1.2, mt: 0.5, fontWeight: 600, textAlign: 'right' }}>
              {endInfo.dateStr}
            </Box>
            <Box sx={{ fontSize: '0.75rem', lineHeight: 1.2, fontWeight: 700, textAlign: 'right' }}>
              {endInfo.time}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}